import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Alert,
  Spinner,
} from "reactstrap";
import toastr from "toastr";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import {
  useGetClientBookingDetails,
  useGetBookingWorkerRequestsPage,
  useCancelBookingKpi,
  useClaimBookingReward,
} from "../../../api/booking";
import { mediaUrl } from "../../../helpers/mediaHelper";
import { getWorkerDisplayName } from "../invite-workers/step1/useStep1SuggestedWorkers";
import {
  parseWorkerRequestsPage,
  getRequestWorkerSlug,
  getAcceptedDatesFromRequest,
  WORKER_REQUEST_FILTERS,
} from "../invite-workers/step3/useStep3Review";
import WorkerMiniProfile from "../../../components/WorkerMiniProfile/WorkerMiniProfile";
import { isEligibleReplacementShift } from "../replacementInviteEligibility";
import {
  formatSmartMatchingMode,
  getShiftRemovalRestrictions,
  hasAnyShiftSmartMatchingActive,
  isShiftSmartMatchingActive,
  validateRemovalAgainstSmartMatching,
} from "../smartMatchingRemovalRules";
import { selectUser } from "../../../store/auth/selectors";
import {
  canAccessInviteWorkers,
  canAccessWorkersManagement,
} from "../clientBookingAccess";

const STATUS_COLOR_MAP = {
  processing: "primary",
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "secondary",
};

export default function ClientBookingWorkersManagement() {
  document.title = "Worker management | Client";

  const { bookingSlug } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [filter, setFilter] = useState(WORKER_REQUEST_FILTERS.accepted);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [removeFlowOpen, setRemoveFlowOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [removeFlowState, setRemoveFlowState] = useState({});

  const {
    data: detailsData,
    isLoading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useGetClientBookingDetails(bookingSlug);

  const booking = useMemo(
    () => detailsData?.data?.data ?? detailsData?.data ?? detailsData ?? null,
    [detailsData]
  );

  const workersManagementAccessDenied =
    !detailsLoading &&
    !detailsError &&
    booking &&
    !canAccessWorkersManagement(booking, user?.tier);

  useEffect(() => {
    if (!workersManagementAccessDenied || !bookingSlug) return;
    navigate(`/client/bookings/${bookingSlug}`, { replace: true });
  }, [workersManagementAccessDenied, bookingSlug, navigate]);

  const showInviteWorkersLink = useMemo(
    () => canAccessInviteWorkers(booking),
    [booking]
  );

  const workerListMut = useGetBookingWorkerRequestsPage();
  const cancelMut = useCancelBookingKpi();
  const claimRewardMut = useClaimBookingReward();

  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({ bookingSlug, filter, page, limit });

  useEffect(() => {
    if (!bookingSlug) return;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    workerListMut.mutate({ bookingSlug, filter, page, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, bookingSlug]);

  const parsedList = useMemo(() => {
    const raw = workerListMut.data;
    return parseWorkerRequestsPage(raw);
  }, [workerListMut.data]);

  const rows = parsedList.items;
  const total = parsedList.total;
  const listLoading = workerListMut.isPending;
  const listError = workerListMut.isError;

  const statusColor = STATUS_COLOR_MAP[booking?.status] || "info";

  const showReplacementCta = useMemo(
    () => (booking?.shiftPattern ?? []).some((s) => isEligibleReplacementShift(s)),
    [booking?.shiftPattern]
  );

  const refreshAll = () => {
    refetchDetails?.();
    workerListMut.mutate({ bookingSlug, filter, page, limit });
  };

  const openProfileModal = useCallback((request) => {
    const user = request?.user;
    if (!user) return;
    setProfileUser({
      ...user,
      worker: request?.worker || user?.worker,
    });
    setIsProfileOpen(true);
  }, []);

  const bookingShiftPatterns = useMemo(
    () => (Array.isArray(booking?.shiftPattern) ? booking.shiftPattern : []),
    [booking?.shiftPattern]
  );

  const anyShiftSmartMatchingActive = useMemo(
    () => hasAnyShiftSmartMatchingActive(bookingShiftPatterns),
    [bookingShiftPatterns]
  );

  const smartMatchingActiveShiftLabels = useMemo(() => {
    return bookingShiftPatterns
      .map((pattern, shiftIndex) => {
        if (!isShiftSmartMatchingActive(pattern)) return null;
        const modeLabel = formatSmartMatchingMode(pattern.smartMatchingActiveMode);
        return `Shift ${shiftIndex + 1} (${modeLabel})`;
      })
      .filter(Boolean);
  }, [bookingShiftPatterns]);

  const shiftPattern = useMemo(() => {
    if (!bookingShiftPatterns.length) return [];
    return bookingShiftPatterns.map((pattern, shiftIndex) => ({
      shiftIndex,
      smartMatchingActiveMode: pattern?.smartMatchingActiveMode ?? null,
      isSmartMatchingActive: isShiftSmartMatchingActive(pattern),
      dates: Array.isArray(pattern?.dates)
        ? pattern.dates
            .map((entry) => (typeof entry === "string" ? entry : entry?.date))
            .filter(Boolean)
        : [],
      workers: rows
        .filter((r) => (r?.shiftIndex ?? 0) === shiftIndex)
        .map((r) => {
          const workerSlug = getRequestWorkerSlug(r);
          const workerDates = getAcceptedDatesFromRequest(r).filter(Boolean);
          return {
            workerSlug,
            label: getWorkerDisplayName({ ...r?.worker, user: r?.user }),
            dates: workerDates,
          };
        })
        .filter((w) => Boolean(w.workerSlug)),
    }));
  }, [bookingShiftPatterns, rows]);

  const resetRemoveFlowState = useCallback(() => {
    const next = {};
    shiftPattern.forEach((shift) => {
      const workers = {};
      shift.workers.forEach((w) => {
        workers[w.workerSlug] = { allDates: false, dates: [] };
      });
      next[shift.shiftIndex] = {
        enabled: false,
        mode: "dates",
        dates: [],
        workers,
        option1: "refund",
        option2: "manual-invite",
      };
    });
    setRemoveFlowState(next);
  }, [shiftPattern]);

  const openRemoveFlowModal = () => {
    resetRemoveFlowState();
    setRemoveFlowOpen(true);
  };

  const updateShiftState = (shiftIndex, updater) => {
    setRemoveFlowState((prev) => {
      const current = prev[shiftIndex] || {
        enabled: false,
        mode: "dates",
        dates: [],
        workers: {},
        option1: "refund",
        option2: "manual-invite",
      };
      const nextShift =
        typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [shiftIndex]: nextShift };
    });
  };

  const buildRemoveFlowPayload = () => {
    const shifts = shiftPattern
      .map((shift) => {
        const state = removeFlowState[shift.shiftIndex];
        if (!state) return null;
        const restrictions = getShiftRemovalRestrictions(
          bookingShiftPatterns[shift.shiftIndex],
          { anyShiftActive: anyShiftSmartMatchingActive }
        );
        if (restrictions.blockAll) return null;
        if (state.enabled) {
          return { shiftIndex: shift.shiftIndex };
        }
        const tabMode =
          restrictions.blockWorkersOnly && (state.mode || "dates") === "workers"
            ? "dates"
            : state.mode || "dates";
        if (tabMode === "dates") {
          if (!state.dates.length) return null;
          return {
            shiftIndex: shift.shiftIndex,
            dates: state.dates,
          };
        }
        if (tabMode === "workers") {
          const workers = shift.workers
            .map((w) => {
              const wState = state.workers?.[w.workerSlug];
              if (!wState) return null;
              const dates = wState.allDates ? w.dates : wState.dates;
              if (!dates?.length) return null;
              return { workerSlug: w.workerSlug, dates };
            })
            .filter(Boolean);
          if (!workers.length) return null;
          const option1 = state.option1 || "refund";
          const isReplacement =
            String(option1).toLowerCase() === "replacement";
          const shiftPayload = {
            shiftIndex: shift.shiftIndex,
            workers,
            option1,
          };
          if (isReplacement) {
            shiftPayload.option2 = state.option2 || "manual-invite";
          }
          return shiftPayload;
        }
        return null;
      })
      .filter(Boolean);

    return { cancelBooking: false, shifts };
  };

  const validateRemoveFlowPayload = (payload) => {
    if (!payload.shifts?.length) {
      toastr.warning("Select at least one shift change before submitting.");
      return false;
    }
    const smCheck = validateRemovalAgainstSmartMatching(
      bookingShiftPatterns,
      payload
    );
    if (!smCheck.valid) {
      toastr.warning(smCheck.message);
      return false;
    }
    const hasInvalid = payload.shifts.some((shift) => {
      const hasDates = Array.isArray(shift.dates) && shift.dates.length > 0;
      const hasWorkers = Array.isArray(shift.workers) && shift.workers.length > 0;
      if (hasDates && hasWorkers) return true;
      if (hasWorkers && !shift.option1) return true;
      if (
        hasWorkers &&
        String(shift.option1).toLowerCase() === "replacement" &&
        !shift.option2
      )
        return true;
      return false;
    });
    if (hasInvalid) {
      toastr.warning(
        "Each shift must have a valid removal and replacement/refund option."
      );
      return false;
    }
    return true;
  };

  const submitCancelBooking = () => {
    if (!bookingSlug) return;
    cancelMut.mutate(
      {
        bookingSlug,
        payload: { cancelBooking: true, type: booking?.bookingType },
      },
      {
        onSuccess: () => {
          toastr.success("Booking cancellation submitted.");
          setCancelConfirmOpen(false);
          refreshAll();
        },
      }
    );
  };

  const submitRemoveFlow = () => {
    if (!bookingSlug) return;
    const payload = buildRemoveFlowPayload();
    if (!validateRemoveFlowPayload(payload)) return;
    cancelMut.mutate(
      { bookingSlug, payload: { ...payload, type: booking?.bookingType } },
      {
        onSuccess: () => {
          toastr.success("Booking updates submitted.");
          setRemoveFlowOpen(false);
          refreshAll();
        },
      }
    );
  };

  const claimRefundedAmount = (type) => {
    if (!bookingSlug) return;
    claimRewardMut.mutate(
      { bookingSlug, type },
      {
        onSuccess: () => {
          toastr.success(
            type === "credit"
              ? "Refunded amount claimed as credit notes."
              : "Refund request submitted."
          );
          setClaimModalOpen(false);
          refetchDetails?.();
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "Worker",
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (info) => {
          const request = info.row.original;
          const name = getWorkerDisplayName({
            ...request?.worker,
            user: request?.user,
          });
          const slug = getRequestWorkerSlug(request);
          const photoRaw =
            request?.user?.photo ||
            request?.worker?.personalInformation?.uploadSelfie;
          const src = photoRaw ? mediaUrl(photoRaw) : null;
          return (
            <div className="d-flex align-items-center gap-2">
              {src ? (
                <img
                  src={src}
                  alt=""
                  className="rounded-circle"
                  width={36}
                  height={36}
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                  style={{ width: 36, height: 36 }}
                >
                  <i className="mdi mdi-account text-muted" />
                </div>
              )}
              <div>
                <div className="fw-semibold">{name}</div>
                <small className="text-muted">{slug || "—"}</small>
              </div>
            </div>
          );
        },
      },
      {
        header: "Shift",
        accessorKey: "shiftIndex",
        enableColumnFilter: false,
        cell: (info) => {
          const idx = info.getValue();
          return typeof idx === "number" ? `Shift ${idx + 1}` : "—";
        },
      },
      {
        header: "Role",
        accessorKey: "role.title",
        enableColumnFilter: false,
        cell: (info) =>
          info.row.original?.role?.title ||
          info.row.original?.role?.name ||
          "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const s = info.getValue();
          return s ? (
            <Badge color="light" className="text-dark text-uppercase">
              {String(s).replace(/_/g, " ")}
            </Badge>
          ) : (
            "—"
          );
        },
      },
      {
        header: "Dates",
        id: "dates",
        enableColumnFilter: false,
        cell: (info) => {
          const request = info.row.original;
          const dates = getAcceptedDatesFromRequest(request);
          if (!dates.length) return <span className="text-muted">—</span>;
          return (
            <span className="small">
              {dates
                .slice(0, 3)
                .map((d) => dayjs(d).format("MMM D"))
                .join(" · ")}
              {dates.length > 3 ? "…" : ""}
            </span>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const request = info.row.original;
          return (
            <Button
              color="primary"
              outline
              size="sm"
              onClick={() => openProfileModal(request)}
            >
              View profile
            </Button>
          );
        },
      },
    ],
    [openProfileModal]
  );

  const shiftSummary = useMemo(() => {
    const patterns = booking?.shiftPattern;
    if (!Array.isArray(patterns) || !patterns.length) return null;
    const totalDates = patterns.reduce(
      (sum, p) => sum + (p.dates?.length || 0),
      0
    );
    return `${patterns.length} shift pattern(s) · ${totalDates} date(s)`;
  }, [booking?.shiftPattern]);

  if (detailsLoading && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Worker management" />
          <Spinners />
        </div>
      </div>
    );
  }

  if (detailsError && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Worker management" />
          <Alert color="danger">Could not load booking details.</Alert>
          <Button color="primary" tag={Link} to="/client/bookings">
            Back to bookings
          </Button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Worker management" />
          <Alert color="warning">Booking not found.</Alert>
          <Button color="primary" tag={Link} to="/client/bookings">
            Back to bookings
          </Button>
        </div>
      </div>
    );
  }

  if (workersManagementAccessDenied) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Worker management" />
          <Spinners />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title="Bookings"
          breadcrumbItem={`Workers · ${booking.slug}`}
        />

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2 flex-wrap">
            <Button color="light" onClick={() => navigate(-1)}>
              <i className="mdi mdi-arrow-left me-1" />
              Back
            </Button>
            <Button
              color="light"
              onClick={() => refreshAll()}
              disabled={listLoading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
            <Button
              color="primary"
              outline
              tag={Link}
              to={`/client/bookings/${bookingSlug}`}
            >
              Booking details
            </Button>
            {showInviteWorkersLink ? (
            <Button
              color="primary"
              outline
              tag={Link}
              to={`/client/bookings/${bookingSlug}/invite-workers`}
            >
              Invite workers
            </Button>
            ) : null}
            {showReplacementCta ? (
              <Button
                color="primary"
                tag={Link}
                to={`/client/bookings/${bookingSlug}/find-replacements`}
              >
                Invite worker for replacement
              </Button>
            ) : null}
          </div>
          <Badge className={`bg-${statusColor} text-uppercase`}>
            {booking.status || "unknown"}
          </Badge>
        </div>

        <Row className="gy-3 mb-3">
          <Col lg={8}>
            <Card>
              <CardHeader>
                <h5 className="mb-0">Booking summary</h5>
              </CardHeader>
              <CardBody>
                <Row className="gy-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Booking ID</small>
                    <div className="fw-semibold">{booking.slug}</div>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Type</small>
                    <div className="text-capitalize">
                      {booking.bookingType?.replace(/_/g, " ") || "—"}
                    </div>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Location</small>
                    <div>
                      {booking.location?.siteName ||
                        booking.location?.fullAddress ||
                        "—"}
                    </div>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Shifts</small>
                    <div>{shiftSummary || "—"}</div>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">PO reference</small>
                    <div>{booking.poReference || "—"}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="h-100">
              <CardHeader>
                <h5 className="mb-0">Booking actions</h5>
              </CardHeader>
              <CardBody>
                <div className="d-grid gap-2">
                  <Button
                    color="danger"
                    outline
                    onClick={() => setCancelConfirmOpen(true)}
                    disabled={cancelMut.isPending}
                  >
                    Cancel booking
                  </Button>
                  <Button
                    color="warning"
                    outline
                    onClick={openRemoveFlowModal}
                    disabled={cancelMut.isPending || !shiftPattern.length}
                  >
                    Remove shifts, dates, workers
                  </Button>
                  {(booking?.totalRefundAmount || 0) > 0 ? (
                    <Button
                      color="success"
                      outline
                      onClick={() => setClaimModalOpen(true)}
                      disabled={claimRewardMut.isPending}
                    >
                      Claim refunded amount
                    </Button>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {anyShiftSmartMatchingActive ? (
          <Alert color="info" className="mb-3">
            <strong>Smart matching in progress.</strong>{" "}
            {smartMatchingActiveShiftLabels.length
              ? `Running for ${smartMatchingActiveShiftLabels.join(", ")}.`
              : "Running on one or more shifts."}{" "}
            Worker removals are paused booking-wide until matching finishes.
            Shifts currently in matching cannot be changed.
          </Alert>
        ) : null}

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="mb-0">Workers</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Input
                type="select"
                className="w-auto"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                  lastFetchKeyRef.current = "";
                }}
              >
                <option value={WORKER_REQUEST_FILTERS.accepted}>
                  Accepted
                </option>
                <option value={WORKER_REQUEST_FILTERS.invited}>Invited</option>
              </Input>
              <Input
                type="select"
                className="w-auto"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                  lastFetchKeyRef.current = "";
                }}
              >
                {[10, 20].map((n) => (
                  <option key={n} value={n}>
                    Show {n}
                  </option>
                ))}
              </Input>
            </div>
          </CardHeader>
          <CardBody>
            {listLoading ? (
              <Spinners />
            ) : listError ? (
              <div className="text-danger">
                Failed to load workers for this booking.
              </div>
            ) : rows.length === 0 ? (
              <div className="text-muted text-center py-4">
                No workers in this list.
              </div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={rows}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />
                {total > 0 && (
                  <Paginations
                    total={total}
                    perPageData={limit}
                    currentPage={page}
                    setCurrentPage={setPage}
                    isShowingPageLength
                    paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                    paginationClass="pagination"
                  />
                )}
              </>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={cancelConfirmOpen}
          toggle={() => !cancelMut.isPending && setCancelConfirmOpen(false)}
          centered
        >
          <ModalHeader
            toggle={() => !cancelMut.isPending && setCancelConfirmOpen(false)}
          >
            Cancel booking
          </ModalHeader>
          <ModalBody>
            This will cancel the entire booking. Are you sure you want to
            continue?
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={() => setCancelConfirmOpen(false)}
              disabled={cancelMut.isPending}
            >
              Keep booking
            </Button>
            <Button
              color="danger"
              onClick={submitCancelBooking}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Cancelling…
                </>
              ) : (
                "Confirm cancel"
              )}
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          isOpen={removeFlowOpen}
          toggle={() => !cancelMut.isPending && setRemoveFlowOpen(false)}
          size="xl"
        >
          <ModalHeader
            toggle={() => !cancelMut.isPending && setRemoveFlowOpen(false)}
          >
            Remove shifts, dates, workers
          </ModalHeader>
          <ModalBody>
            <div className="d-flex flex-column gap-3">
              {shiftPattern.map((shift) => {
                const state = removeFlowState[shift.shiftIndex] || {};
                const mode = state.mode || "dates";
                const deleteShift = Boolean(state.enabled);
                const restrictions = getShiftRemovalRestrictions(
                  bookingShiftPatterns[shift.shiftIndex],
                  { anyShiftActive: anyShiftSmartMatchingActive }
                );
                const blockAll = restrictions.blockAll;
                const blockWorkersOnly = restrictions.blockWorkersOnly;
                const effectiveMode =
                  blockWorkersOnly && mode === "workers" ? "dates" : mode;
                const modeLabel = formatSmartMatchingMode(
                  shift.smartMatchingActiveMode
                );
                return (
                  <Card key={shift.shiftIndex} className="border">
                    <CardBody>
                      {blockAll ? (
                        <Alert color="warning" className="mb-3 py-2">
                          Smart matching is in progress for this shift (
                          {modeLabel}). Removals are disabled until it
                          completes.
                        </Alert>
                      ) : blockWorkersOnly ? (
                        <Alert color="info" className="mb-3 py-2">
                          Worker removal is disabled while smart matching runs
                          on another shift. You can still remove dates or delete
                          this shift.
                        </Alert>
                      ) : null}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-semibold">
                          Shift {shift.shiftIndex + 1}
                        </div>
                        <div className="form-check mb-0">
                          <Input
                            type="checkbox"
                            id={`remove-delete-shift-${shift.shiftIndex}`}
                            className="form-check-input"
                            checked={deleteShift}
                            disabled={blockAll || cancelMut.isPending}
                            onChange={(e) =>
                              updateShiftState(shift.shiftIndex, (prev) => ({
                                ...prev,
                                enabled: e.target.checked,
                                mode: e.target.checked
                                  ? null
                                  : prev.mode || "dates",
                                dates: e.target.checked ? [] : prev.dates,
                                workers: Object.fromEntries(
                                  Object.keys(prev.workers || {}).map((key) => [
                                    key,
                                    { allDates: false, dates: [] },
                                  ])
                                ),
                                option1: e.target.checked
                                  ? "refund"
                                  : prev.option1 || "refund",
                                option2: e.target.checked
                                  ? "manual-invite"
                                  : prev.option2 || "manual-invite",
                              }))
                            }
                          />
                          <Label
                            className="form-check-label"
                            htmlFor={`remove-delete-shift-${shift.shiftIndex}`}
                          >
                            Delete shift
                          </Label>
                        </div>
                      </div>
                      {deleteShift ? (
                        <div className="small text-muted">
                          This shift will be removed entirely.
                        </div>
                      ) : blockAll ? (
                        <div className="small text-muted mb-0">
                          No removal options are available for this shift.
                        </div>
                      ) : (
                        <>
                          <div className="d-flex w-100 mb-3">
                            <Button
                              color={effectiveMode === "dates" ? "primary" : "light"}
                              className="w-50 rounded-0"
                              disabled={blockAll || cancelMut.isPending}
                              onClick={() =>
                                updateShiftState(shift.shiftIndex, (prev) => ({
                                  ...prev,
                                  mode: "dates",
                                  workers: Object.fromEntries(
                                    Object.keys(prev.workers || {}).map(
                                      (key) => [
                                        key,
                                        { allDates: false, dates: [] },
                                      ]
                                    )
                                  ),
                                  option1: "refund",
                                  option2: "manual-invite",
                                }))
                              }
                            >
                              Remove Dates
                            </Button>
                            <Button
                              color={effectiveMode === "workers" ? "primary" : "light"}
                              className="w-50 rounded-0"
                              disabled={
                                blockAll || blockWorkersOnly || cancelMut.isPending
                              }
                              onClick={() =>
                                updateShiftState(shift.shiftIndex, (prev) => ({
                                  ...prev,
                                  mode: "workers",
                                  dates: [],
                                }))
                              }
                            >
                              Remove Workers
                            </Button>
                          </div>
                          {effectiveMode === "dates" ? (
                            shift.dates.length ? (
                              shift.dates.map((date) => {
                              const dateInputId = `remove-date-${shift.shiftIndex}-${date}`;
                              return (
                                <div
                                  className="form-check mb-2"
                                  key={date}
                                >
                                  <Input
                                    type="checkbox"
                                    id={dateInputId}
                                    className="form-check-input"
                                    checked={Boolean(
                                      state.dates?.includes(date)
                                    )}
                                    disabled={blockAll}
                                    onChange={(e) =>
                                      updateShiftState(
                                        shift.shiftIndex,
                                        (prev) => {
                                          const nextDates = new Set(
                                            prev.dates || []
                                          );
                                          if (e.target.checked)
                                            nextDates.add(date);
                                          else nextDates.delete(date);
                                          return {
                                            ...prev,
                                            dates: Array.from(nextDates),
                                          };
                                        }
                                      )
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={dateInputId}
                                  >
                                    {dayjs(date).format("MMM D, YYYY")}
                                  </Label>
                                </div>
                              );
                            })
                            ) : (
                              <div className="small text-muted">
                                No shift dates available.
                              </div>
                            )
                          ) : (
                            <div>
                              {!shift.workers.length ? (
                                <div className="small text-muted mb-2">
                                  No workers found for this shift.
                                </div>
                              ) : (
                                shift.workers.map((worker) => {
                                  const workerState = state.workers?.[
                                    worker.workerSlug
                                  ] || {
                                    allDates: false,
                                    dates: [],
                                  };
                                  return (
                                    <Card
                                      key={worker.workerSlug}
                                      className="border mb-2"
                                    >
                                      <CardBody className="py-2">
                                        <div className="fw-semibold small mb-2">
                                          {worker.label}
                                        </div>
                                        <div className="form-check mb-2">
                                          <Input
                                            type="checkbox"
                                            id={`remove-worker-all-${shift.shiftIndex}-${worker.workerSlug}`}
                                            className="form-check-input"
                                            checked={workerState.allDates}
                                            disabled={blockAll}
                                            onChange={(e) =>
                                              updateShiftState(
                                                shift.shiftIndex,
                                                (prev) => ({
                                                  ...prev,
                                                  workers: {
                                                    ...prev.workers,
                                                    [worker.workerSlug]: {
                                                      ...workerState,
                                                      allDates:
                                                        e.target.checked,
                                                      dates: e.target.checked
                                                        ? []
                                                        : workerState.dates,
                                                    },
                                                  },
                                                })
                                              )
                                            }
                                          />
                                          <Label
                                            className="form-check-label"
                                            htmlFor={`remove-worker-all-${shift.shiftIndex}-${worker.workerSlug}`}
                                          >
                                            Remove full worker
                                          </Label>
                                        </div>
                                        <div
                                          className={
                                            workerState.allDates
                                              ? "opacity-50"
                                              : ""
                                          }
                                        >
                                          {worker.dates.map((date) => {
                                            const workerDateInputId = `remove-worker-date-${shift.shiftIndex}-${worker.workerSlug}-${date}`;
                                            return (
                                              <div
                                                className="form-check"
                                                key={`${worker.workerSlug}-${date}`}
                                              >
                                                <Input
                                                  type="checkbox"
                                                  id={workerDateInputId}
                                                  className="form-check-input"
                                                  checked={workerState.dates.includes(
                                                    date
                                                  )}
                                                  disabled={
                                                    blockAll ||
                                                    workerState.allDates
                                                  }
                                                  onChange={(e) =>
                                                    updateShiftState(
                                                      shift.shiftIndex,
                                                      (prev) => {
                                                        const current =
                                                          prev.workers?.[
                                                            worker.workerSlug
                                                          ] || workerState;
                                                        const nextDates =
                                                          new Set(
                                                            current.dates || []
                                                          );
                                                        if (e.target.checked)
                                                          nextDates.add(date);
                                                        else
                                                          nextDates.delete(
                                                            date
                                                          );
                                                        return {
                                                          ...prev,
                                                          workers: {
                                                            ...prev.workers,
                                                            [worker.workerSlug]:
                                                              {
                                                                ...current,
                                                                dates:
                                                                  Array.from(
                                                                    nextDates
                                                                  ),
                                                              },
                                                          },
                                                        };
                                                      }
                                                    )
                                                  }
                                                />
                                                <Label
                                                  className="form-check-label"
                                                  htmlFor={workerDateInputId}
                                                >
                                                  {dayjs(date).format(
                                                    "MMM D, YYYY"
                                                  )}
                                                </Label>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </CardBody>
                                    </Card>
                                  );
                                })
                              )}
                              <div className="mt-2">
                                <Label className="form-label d-block mb-2">
                                  Replacement or refund
                                </Label>
                                <div className="form-check form-check-inline">
                                  <Input
                                    type="radio"
                                    id={`remove-option1-replacement-${shift.shiftIndex}`}
                                    className="form-check-input"
                                    name={`shift-option1-${shift.shiftIndex}`}
                                    checked={
                                      (state.option1 || "refund") ===
                                      "replacement"
                                    }
                                    disabled={blockAll}
                                    onChange={() =>
                                      updateShiftState(
                                        shift.shiftIndex,
                                        (prev) => ({
                                          ...prev,
                                          option1: "replacement",
                                        })
                                      )
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={`remove-option1-replacement-${shift.shiftIndex}`}
                                  >
                                    Find replacement
                                  </Label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <Input
                                    type="radio"
                                    id={`remove-option1-refund-${shift.shiftIndex}`}
                                    className="form-check-input"
                                    name={`shift-option1-${shift.shiftIndex}`}
                                    checked={
                                      (state.option1 || "refund") === "refund"
                                    }
                                    disabled={blockAll}
                                    onChange={() =>
                                      updateShiftState(
                                        shift.shiftIndex,
                                        (prev) => ({
                                          ...prev,
                                          option1: "refund",
                                          option2: "manual-invite",
                                        })
                                      )
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={`remove-option1-refund-${shift.shiftIndex}`}
                                  >
                                    Take refund
                                  </Label>
                                </div>
                              </div>
                              {(state.option1 || "refund") === "replacement" ? (
                                <div className="mt-2">
                                  <Label className="form-label">
                                    Replacement method
                                  </Label>
                                  <Input
                                    type="select"
                                    value={state.option2 || "manual-invite"}
                                    disabled={blockAll}
                                    onChange={(e) =>
                                      updateShiftState(
                                        shift.shiftIndex,
                                        (prev) => ({
                                          ...prev,
                                          option2: e.target.value,
                                        })
                                      )
                                    }
                                  >
                                    <option value="manual-invite">
                                      Manual invite
                                    </option>
                                    <option value="smart-matching">
                                      Smart matching
                                    </option>
                                  </Input>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={() => setRemoveFlowOpen(false)}
              disabled={cancelMut.isPending}
            >
              Close
            </Button>
            <Button
              color="danger"
              onClick={submitRemoveFlow}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting…
                </>
              ) : (
                "Submit changes"
              )}
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          isOpen={claimModalOpen}
          toggle={() => !claimRewardMut.isPending && setClaimModalOpen(false)}
          centered
        >
          <ModalHeader
            toggle={() => !claimRewardMut.isPending && setClaimModalOpen(false)}
          >
            Claim refunded amount
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              Available refunded amount:{" "}
              <span className="fw-semibold">
                {booking?.totalRefundAmount || 0}
              </span>
            </p>
            <p className="small text-muted mb-0">
              Choose whether to receive this as a direct refund or claim it as
              credit notes.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={() => claimRefundedAmount("credit")}
              disabled={claimRewardMut.isPending}
            >
              Claim as credit
            </Button>
            <Button
              color="success"
              onClick={() => claimRefundedAmount("refund")}
              disabled={claimRewardMut.isPending}
            >
              Claim as refund
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          isOpen={isProfileOpen}
          toggle={() => setIsProfileOpen(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setIsProfileOpen(false)}>
            Worker profile
          </ModalHeader>
          <ModalBody>
            {profileUser ? <WorkerMiniProfile user={profileUser} /> : null}
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
